import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NgZone,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { CrcPipe } from '../../../shared/pipes/crc.pipe';
import { HttpErrorResponse } from '@angular/common/http';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { CountdownComponent } from '../../../shared/ui/countdown/countdown.component';
import { PaymentService } from '../../../shared/services/payment.service';
import { CartService } from '../../../shared/services/cart.service';
import { BlockingLoaderService } from '../../../shared/services/blocking-loader.service';
import { PAYMENT_MAX_ATTEMPTS } from '../../../shared/payments/payment.config';
import {
  CheckoutIntentResult,
  PaymentStatus,
} from '../../../shared/payments/payment-provider.interface';
import { PAYMENT_PROVIDER } from '../../../shared/payments/payment-provider.token';
import { OnvoPaymentProvider } from '../../../shared/payments/onvo-payment.provider';
import { CHECKOUT_COPY } from '../checkout-copy';
import {
  OnvoErrorData,
  OnvoFormErrorsData,
  OnvoGlobal,
  OnvoInstance,
  OnvoSuccessData,
} from '../../../shared/payments/onvo-sdk.types';

type PaymentNavigationState = {
  /** Intent recién creado por el checkout (camino feliz, sin re-fetch). */
  checkoutIntent?: CheckoutIntentResult;
  preorderHasSingles?: boolean;
};

/** Intent vivo mínimo que necesita el widget. */
type LiveIntent = { providerIntentId: string; amountColones: number };

/**
 * Fases del paso de pago. `creating` (bootstrap) → `ready` (intent listo, widget montable)
 * → `confirming` → navega a éxito | `pending` (webhook materializará) | `failed` (reintento/volver).
 */
type PaymentPhase = 'creating' | 'ready' | 'confirming' | 'pending' | 'failed';

/** Causa del fallo terminal del paso, para elegir copy. */
type PaymentFailureKind = 'create' | 'timeout' | 'network' | 'decline';

/** Copy de cara al cliente del paso de tarjeta (centralizada en checkout-copy.ts). */
const CARD_COPY = CHECKOUT_COPY.cardPayment;

/** Copys para sesiones que ya no admiten volver a montar el intent. */
const TERMINAL_SESSION_COPY: Record<'expired' | 'cancelled' | 'refunded', string> = {
  expired: CARD_COPY.sessionExpired,
  cancelled: CARD_COPY.sessionCancelled,
  refunded: CARD_COPY.sessionRefunded,
};

/** Reintentos automáticos de `complete` ante 422 (ONVO aún no marca succeeded tras el onSuccess). */
const COMPLETE_MAX_RETRIES = 2;
const COMPLETE_RETRY_DELAY_MS = 1500;

/** URL del SDK web de ONVO (widget hospedado). */
const ONVO_SDK_SRC = 'https://sdk.onvopay.com/sdk.js';

/**
 * Paso de pago con tarjeta del flujo DIFERIDO (`/checkout/pago/sesion/:sessionToken`). El preorder
 * NO existe todavía: el intent cuelga de una `checkout_session`. Solo al aprobarse el pago se
 * materializa el pedido (vía `completeCheckout` o el webhook). El carrito permanece intacto: si el
 * pago falla, el cliente puede reintentar sin perderlo.
 *
 * Fix del bug «Document is ready and element [data-payment-mount] does not exist»: el widget se monta
 * desde un `effect` que espera a que el nodo `[data-payment-mount]` exista en el DOM (viewChild), en
 * vez de llamar a `render()` justo tras cargar el SDK (que en la 2ª vez resolvía antes del commit del
 * DOM). ONVO renderiza en un nodo dedicado e interno, que Angular no gestiona, para poder limpiarlo
 * al reintentar sin corromper la vista.
 */
@Component({
  selector: 'app-checkout-payment-page',
  imports: [CrcPipe, ButtonComponent, CountdownComponent],
  templateUrl: './checkout-payment-page.component.html',
  styleUrl: './checkout-payment-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CheckoutPaymentPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly paymentService = inject(PaymentService);
  private readonly cartService = inject(CartService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef) as ElementRef<HTMLElement>;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly provider = inject(PAYMENT_PROVIDER);
  private readonly ngZone = inject(NgZone);
  private readonly blockingLoader = inject(BlockingLoaderService);

  /** Promesa cacheada de carga del SDK (compartida entre instancias; null si aún no se cargó). */
  private static sdkPromise: Promise<OnvoGlobal> | null = null;

  /** Nodo dedicado donde ONVO pinta el widget (fuera del control de Angular). */
  private readonly paymentMount = viewChild<ElementRef<HTMLElement>>('paymentMount');

  /** Instancia viva del widget de ONVO. `null` mientras no esté montado. */
  private onvoInstance: OnvoInstance | null = null;

  /** Guard de montaje único (se reinicia en retry/teardown). No es signal a propósito. */
  private mounted = false;

  /** Marca de destrucción: evita tocar el DOM/estado tras `onDestroy` o si el SDK resuelve tarde. */
  private destroyed = false;

  /** Token de la `checkout_session` (de la URL). Identidad del pago hasta materializar el pedido. */
  private readonly sessionToken = this.route.snapshot.paramMap.get('sessionToken') ?? '';

  /** Intent recibido por router-state (camino feliz), si lo hay. */
  private readonly stateIntent =
    (this.router.getCurrentNavigation()?.extras?.state as PaymentNavigationState | null | undefined)
      ?.checkoutIntent ?? null;

  /** Bandera de singles para conservar el texto correcto en la pantalla de éxito. */
  private readonly preorderHasSingles =
    (this.router.getCurrentNavigation()?.extras?.state as
      | PaymentNavigationState
      | null
      | undefined)?.preorderHasSingles ?? null;

  protected readonly phase = signal<PaymentPhase>('creating');
  protected readonly intent = signal<LiveIntent | null>(null);
  protected readonly submitting = signal(false);
  /**
   * `true` SOLO durante la materialización en el backend (post-`onSuccess`, ya
   * superado el 3DS): gobierna el overlay bloqueante. Se mantiene separado de
   * `submitting` para que el overlay NO tape el reto 3DS, que ocurre antes.
   */
  protected readonly completing = signal(false);
  protected readonly errorMsg = signal<string | null>(null);

  /** Fallos terminales reintentables (decline/create). Cap único. */
  protected readonly attempts = signal(0);
  /** `true` para fallos terminales sin sentido de reintento (sin stock → reembolsado). */
  protected readonly noRetry = signal(false);
  protected readonly canRetry = computed(
    () => !this.noRetry() && this.attempts() < PAYMENT_MAX_ATTEMPTS,
  );

  /** `true` cuando el SDK montó sus campos de tarjeta y puede tokenizar. */
  protected readonly cardReady = signal(false);

  /** `true` mientras se comprueba el estado de la sesión desde el panel pending (feedback del botón). */
  protected readonly checking = signal(false);

  /** Monto a cobrar (derivado server-side; única fuente de verdad). */
  protected readonly amountColones = computed(() => this.intent()?.amountColones ?? 0);

  /**
   * Expiración ABSOLUTA (ISO 8601) del HOLD de stock (secuestro por sesión de pago): backend-
   * autoritativa, alimenta el countdown de la página. `null` = sin hold (sesión previa a la feature)
   * → no se pinta countdown. Spec backend:
   * perronstore-api/docs/specs/checkout-stock-flotante-pedidos-plan.md.
   */
  protected readonly reservationExpiresAt = signal<string | null>(null);
  /** Copy del countdown del hold (lleva `{mm:ss}`; el instante lo da el backend). */
  protected readonly reservationTimerLabel = CHECKOUT_COPY.reservation.timer;

  /** Copy de cara al cliente para la plantilla (título y frases de tranquilidad del panel). */
  protected readonly cardCopy = CARD_COPY;

  constructor() {
    // SSR-safe: el bootstrap (router-state o fetch de la sesión) corre en el navegador.
    afterNextRender(() => this.bootstrap());

    // A11y: al cambiar a un panel con encabezado propio, llevamos el foco a su <h1>.
    effect(() => {
      const p = this.phase();
      if (!isPlatformBrowser(this.platformId)) return;
      if (p === 'creating' || p === 'confirming') return;
      setTimeout(() => {
        this.host.nativeElement
          .querySelector<HTMLElement>('[data-phase-heading]')
          ?.focus({ preventScroll: true });
      });
    });

    // Montaje del widget: SOLO cuando el nodo mount está en el DOM (viewChild resuelto) y hay intent.
    // Esto cierra la carrera del render: ya no llamamos a render() antes de que Angular committee el
    // nodo (causa del error «element [data-payment-mount] does not exist» en el 2º intento).
    effect(() => {
      const el = this.paymentMount();
      if (!isPlatformBrowser(this.platformId)) return;
      if (this.phase() !== 'ready') return;
      const intent = this.intent();
      if (!intent || !el || this.mounted) return;
      this.mounted = true;
      void this.mountWidget(el.nativeElement);
    });

    // Overlay global BLOQUEANTE durante la materialización del pago en el backend
    // (post-3DS): cubre toda la web (incluido el header/menú) e impide al cliente
    // tocar nada o navegar y romper el ciclo. La fuente de verdad es `completing()`
    // —NO `submitting()`— para que el overlay NO tape el reto 3DS, que ocurre antes
    // de `onSuccess`. Se libera en cada salida y al destruir el componente.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      if (this.completing()) {
        this.blockingLoader.show();
      } else {
        this.blockingLoader.hide();
      }
    });

    // Aviso del navegador si se intenta recargar/cerrar la pestaña en pleno pago.
    afterNextRender(() => {
      fromEvent<BeforeUnloadEvent>(window, 'beforeunload')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.submitting()) return;
          e.preventDefault();
          e.returnValue = '';
        });
    });

    // Limpieza del widget al destruir el componente (navegación/refresh).
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.blockingLoader.hide();
      this.teardownWidget();
    });
  }

  /**
   * Prepara el intent: reusa el de router-state (camino feliz) o lo recupera de la sesión (refresh/
   * enlace directo). Si la sesión ya se materializó, redirige a la pantalla de éxito.
   */
  private bootstrap(): void {
    if (!this.sessionToken) {
      this.fail('create', 'No encontramos tu pago. Vuelve al carrito e inténtalo de nuevo.');
      return;
    }

    if (this.stateIntent && this.stateIntent.sessionToken === this.sessionToken) {
      this.reservationExpiresAt.set(this.stateIntent.reservationExpiresAt ?? null);
      this.resumeIntent(
        {
          providerIntentId: this.stateIntent.providerIntentId,
          amountColones: this.stateIntent.amountColones,
        },
        this.stateIntent.status,
      );
      return;
    }

    // Refresh / enlace directo: recuperar el estado vivo de la sesión.
    this.phase.set('creating');
    this.paymentService
      .getCheckoutSession(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => {
          if (s.consumed && s.preorderToken) {
            // El pago ya se materializó (otra pestaña/webhook): vaciar el carrito antes de ir a éxito,
            // igual que los demás caminos hacia /checkout/exito.
            this.cartService.clearCart();
            this.goToSuccess(s.preorderToken);
            return;
          }
          if (s.consumed) {
            this.failWithoutRetry(
              'El pago ya fue procesado, pero no pudimos recuperar el pedido. Contáctanos para revisarlo.',
            );
            return;
          }
          if (!s.providerIntentId) {
            this.fail('create');
            return;
          }
          this.reservationExpiresAt.set(s.reservationExpiresAt ?? null);
          this.resumeIntent(
            {
              providerIntentId: s.providerIntentId,
              amountColones: s.amountColones,
            },
            s.status,
          );
        },
        error: () => this.fail('create'),
      });
  }

  /**
   * Rehidrata una sesión según el estado autoritativo del intent. Solo los estados vivos montan el
   * widget; un cobro aprobado se materializa de inmediato y los estados terminales muestran salida.
   */
  private resumeIntent(intent: LiveIntent, status: PaymentStatus = 'pending'): void {
    this.intent.set(intent);

    if (status === 'approved') {
      this.submitting.set(true);
      this.completing.set(true);
      this.phase.set('confirming');
      this.completeWithRetry(0);
      return;
    }

    if (status === 'expired' || status === 'cancelled' || status === 'refunded') {
      this.failWithoutRetry(TERMINAL_SESSION_COPY[status]);
      return;
    }

    if (status === 'declined') {
      this.fail('decline', CARD_COPY.generic);
      return;
    }

    if (status === 'error') {
      this.fail('network');
      return;
    }

    this.phase.set('ready');
  }

  /** CTA «Pagar»: dispara la tokenización + confirmación del widget (`manualSubmit: true`). */
  protected onPayClick(): void {
    if (this.submitting() || !this.cardReady() || !this.onvoInstance) return;
    this.submitting.set(true);
    this.errorMsg.set(null);
    this.phase.set('confirming');
    this.onvoInstance.submitPayment();
  }

  /** Publishable key de ONVO si la pasarela activa es ONVO; '' en otro caso. */
  private get onvoPublishableKey(): string {
    return this.provider.id === 'onvo'
      ? (this.provider as OnvoPaymentProvider).publishableKey
      : '';
  }

  /** Carga el SDK web de ONVO (una sola vez, SSR-safe). No cachea el fallo. */
  private loadSdk(): Promise<OnvoGlobal> {
    if (!isPlatformBrowser(this.platformId)) return Promise.reject(new Error('onvo-ssr'));
    if (window.onvo) return Promise.resolve(window.onvo);
    if (CheckoutPaymentPageComponent.sdkPromise) return CheckoutPaymentPageComponent.sdkPromise;

    const promise = new Promise<OnvoGlobal>((resolve, reject) => {
      const onReady = () =>
        window.onvo ? resolve(window.onvo) : reject(new Error('onvo-undefined'));
      const onError = () => reject(new Error('onvo-load-failed'));

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${ONVO_SDK_SRC}"]`);
      if (existing) {
        if (window.onvo) return resolve(window.onvo);
        existing.addEventListener('load', onReady, { once: true });
        existing.addEventListener('error', onError, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = ONVO_SDK_SRC;
      script.async = true;
      script.addEventListener('load', onReady, { once: true });
      script.addEventListener('error', onError, { once: true });
      document.head.appendChild(script);
    }).catch((err) => {
      CheckoutPaymentPageComponent.sdkPromise = null;
      throw err;
    });

    CheckoutPaymentPageComponent.sdkPromise = promise;
    return promise;
  }

  /**
   * Monta el widget de ONVO sobre el nodo `el` (garantizado en el DOM por el effect). Limpia
   * cualquier render previo del nodo antes de re-montar (evita iframes acumulados al reintentar).
   */
  private async mountWidget(el: HTMLElement): Promise<void> {
    const intent = this.intent();
    if (!intent) {
      // Defensivo (rama muerta hoy): libera el guard para no bloquear un futuro re-montaje.
      this.mounted = false;
      return;
    }

    const publicKey = this.onvoPublishableKey;
    if (!publicKey) {
      this.fail('create');
      return;
    }

    try {
      const onvo = await this.loadSdk();
      if (this.destroyed) return;
      // FUERA de la zona de Angular: zoid arranca tareas PERPETUAS (iframe interno
      // de cierre con `setInterval`, listeners de `postMessage`, ThreatMetrix). Si
      // viven en la zona, `ApplicationRef.isStable()` nunca emite `true` → la
      // hidratación SSR caduca (NG0506) y se dispara CD en cada tick. Los callbacks
      // RE-ENTRAN a la zona (`ngZone.run`) para que la detección de cambios reaccione
      // al resultado del pago (la app es zone-based).
      this.ngZone.runOutsideAngular(() => {
        el.replaceChildren();
        this.onvoInstance = onvo.pay({
          publicKey,
          paymentIntentId: intent.providerIntentId,
          paymentType: 'one_time',
          manualSubmit: true,
          locale: 'es',
          onSuccess: (data) => this.ngZone.run(() => this.onWidgetSuccess(data)),
          onError: (data) => this.ngZone.run(() => this.onWidgetError(data)),
          onFormErrors: (data) => this.ngZone.run(() => this.onWidgetFormErrors(data)),
        });
        this.onvoInstance.render('[data-payment-mount]');
      });
      // De vuelta en la zona: habilita el CTA y oculta el placeholder (necesita CD).
      this.cardReady.set(true);
    } catch {
      this.mounted = false;
      if (!this.destroyed) this.fail('create');
    }
  }

  /**
   * Éxito del widget: el cobro se confirmó en el navegador. Pedimos al backend que VERIFIQUE con ONVO
   * y MATERIALICE el preorder (`completeCheckout`). Solo entonces vaciamos el carrito y vamos a éxito.
   */
  private onWidgetSuccess(_data: OnvoSuccessData): void {
    // El cobro se autorizó en el navegador (3DS ya superado): ahora SÍ bloqueamos
    // la pantalla mientras el backend materializa el preorder.
    this.completing.set(true);
    this.phase.set('confirming');
    this.completeWithRetry(0);
  }

  private completeWithRetry(attempt: number): void {
    this.paymentService
      .completeCheckout(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ token }) => {
          this.submitting.set(false);
          this.completing.set(false);
          // El pedido ya existe y está pagado: ahora sí vaciamos el carrito.
          this.cartService.clearCart();
          this.goToSuccess(token);
        },
        error: (err: unknown) => {
          const status = err instanceof HttpErrorResponse ? err.status : 0;

          // Cobrado pero sin stock → reembolsado. Terminal, sin reintento de tarjeta.
          if (status === 409) {
            this.failWithoutRetry(CARD_COPY.outOfStock);
            return;
          }

          // ONVO aún no marca 'succeeded' tras el onSuccess (carrera breve): reintentar el complete.
          if (status === 422 && attempt < COMPLETE_MAX_RETRIES && !this.destroyed) {
            setTimeout(() => {
              if (!this.destroyed) this.completeWithRetry(attempt + 1);
            }, COMPLETE_RETRY_DELAY_MS);
            return;
          }

          // 422 agotado o red/timeout: el cobro probablemente entró; el webhook materializará el
          // pedido. Panel pendiente con opción de comprobar el estado.
          this.submitting.set(false);
          this.completing.set(false);
          this.phase.set('pending');
        },
      });
  }

  /** Error/declinada del widget → fallo reintentable (carrito intacto). */
  private onWidgetError(data: OnvoErrorData): void {
    this.fail('decline', this.localizeOnvoError(data));
  }

  /**
   * Validación de campos fallida (nombre/número/fecha/CVV inválidos). No es un
   * decline: el SDK ya muestra los errores inline, así que NO pasamos a 'failed'
   * ni añadimos copy. Solo salimos de 'confirming' y reactivamos el CTA para que
   * el cliente corrija y reintente sin recargar (el widget sigue montado).
   */
  private onWidgetFormErrors(_data: OnvoFormErrorsData): void {
    this.submitting.set(false);
    if (this.phase() === 'confirming') this.phase.set('ready');
  }

  /** Mapea el `error.code` de ONVO a copy localizado (default genérico). */
  private localizeOnvoError(data: OnvoErrorData): string {
    switch (data?.error?.code) {
      case 'card_declined':
        return CARD_COPY.declined;
      case 'insufficient_funds':
        return CARD_COPY.insufficientFunds;
      case 'expired_card':
        return CARD_COPY.expiredCard;
      case 'incorrect_cvc':
        return CARD_COPY.incorrectCvc;
      case 'processing_error':
        return CARD_COPY.processingError;
      default:
        return CARD_COPY.generic;
    }
  }

  /** Destruye la instancia del widget y limpia el nodo (idempotente). */
  private teardownWidget(): void {
    this.onvoInstance?.destroy?.();
    this.onvoInstance?.unmount?.();
    this.onvoInstance = null;
    this.mounted = false;
    this.paymentMount()?.nativeElement?.replaceChildren();
  }

  /** Centraliza el fallo terminal: cuenta el intento, pasa a `failed` y elige copy por causa. */
  private fail(kind: PaymentFailureKind, message?: string | null): void {
    this.submitting.set(false);
    this.completing.set(false);
    this.attempts.update((n) => n + 1);
    this.phase.set('failed');
    this.errorMsg.set(this.canRetry() ? this.errorCopy(kind, message) : CARD_COPY.escalation);
  }

  /** Falla de forma terminal cuando reintentar el mismo intent ya no es seguro ni útil. */
  private failWithoutRetry(message: string): void {
    this.submitting.set(false);
    this.completing.set(false);
    this.noRetry.set(true);
    this.errorMsg.set(message);
    this.phase.set('failed');
  }

  private errorCopy(kind: PaymentFailureKind, message?: string | null): string {
    switch (kind) {
      case 'create':
        return CARD_COPY.createFailed;
      case 'timeout':
        return CARD_COPY.timeout;
      case 'network':
        return CARD_COPY.network;
      case 'decline':
        return message ?? CARD_COPY.declineFallback;
    }
  }

  /** Reintento sobre el MISMO intent: re-monta el widget desde cero (carrito intacto). */
  protected retry(): void {
    if (!this.canRetry()) return;
    this.errorMsg.set(null);
    this.cardReady.set(false);
    this.teardownWidget();
    if (this.intent()) {
      this.phase.set('ready');
    } else {
      this.bootstrap();
    }
  }

  /** Comprueba si el webhook ya materializó el pedido (desde el panel `pending`). Con feedback. */
  protected checkSession(): void {
    if (this.checking()) return;
    this.checking.set(true);
    this.errorMsg.set(null);
    this.paymentService
      .getCheckoutSession(this.sessionToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => {
          if (s.consumed && s.preorderToken) {
            this.cartService.clearCart();
            this.goToSuccess(s.preorderToken);
            return;
          }
          this.checking.set(false);
          this.errorMsg.set('Aún estamos confirmando tu pago. Vuelve a comprobar en unos minutos.');
        },
        error: () => {
          this.checking.set(false);
          this.errorMsg.set('No pudimos comprobar el estado ahora. Revisa tu conexión e inténtalo de nuevo.');
        },
      });
  }

  /**
   * El HOLD de stock expiró (5 min) con el widget montado ANTES de iniciar la confirmación: hacemos
   * teardown del widget y mostramos el aviso de expiración (el cliente vuelve al carrito y re-reserva).
   * Solo actúa en fase 'ready': si ya está confirmando/pending/failed, el hold expirado es irrelevante
   * (la red raw de materialize decide, D9). Espejo de la lógica del modal SINPE (onReservationExpired).
   */
  protected onReservationExpired(): void {
    if (this.phase() !== 'ready') return;
    this.teardownWidget();
    this.reservationExpiresAt.set(null);
    this.failWithoutRetry(CHECKOUT_COPY.reservation.expired);
  }

  /** Vuelve al checkout con el carrito intacto (fallback cuando el pago no se completó). */
  protected backToCheckout(): void {
    this.router.navigate(['/checkout']);
  }

  /** Navega a la confirmación con el token del preorder ya materializado. */
  private goToSuccess(token: string): void {
    const state: PaymentNavigationState = {};
    if (typeof this.preorderHasSingles === 'boolean') {
      state.preorderHasSingles = this.preorderHasSingles;
    }
    this.router.navigate(['/checkout/exito', token], { state, replaceUrl: true });
  }
}
